import { IReportSection, MarkerClasses, SectionIds } from '@modeso/aumico-lib-annualreport-fe';
import { EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe/src/lib';
import {
    AnnualReportStep,
    IAnnualReport,
    IComplementaryReport,
    IDisplayOptions,
    PrintLocation,
} from '@modeso/types__aumico-lib-annualreport-be';
import { cloneDeep } from 'lodash-es';
import { IComplementaryReportIdLocationSortOrder } from 'projects/aumico-project-reports/src/app/interfaces/complementary-report-id-location-sort-order.interface';
import { PDFSectionIds } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/enums/pdf-tron/pdf-section-ids.enum';
import { SECTION_ID_TO_SECTION_MAP } from './report-sections-map';

export class ReportSectionsHelper {
    private static getComplementaryReportsSelection(
        printOptions: IDisplayOptions,
    ): IDisplayOptions['complementaryReportsSelection'] {
        return printOptions.complementaryReportsSelection ?? [];
    }

    public static getReportSections(
        predifinedLables: Map<string, string>,
        printOptions: IDisplayOptions,
        activeArNavigations: AnnualReportStep[],
        complementaryReportsTitles: string[],
        annualReport: IAnnualReport,
        isFixedAssetsScheduleDataValid: boolean,
    ): IReportSection[] {
        const complementaryReportsSelection =
            ReportSectionsHelper.getComplementaryReportsSelection(printOptions);
        const reportSectionIds = ReportSectionsHelper.getOrderedSectionIds(
            printOptions,
            activeArNavigations,
            complementaryReportsTitles,
            annualReport,
            isFixedAssetsScheduleDataValid,
            complementaryReportsSelection,
        );

        const reportSections: IReportSection[] = [];

        reportSectionIds.forEach((sectionId) => {
            let section: IReportSection;
            if (!sectionId.includes(SectionIds.COMPLEMENTARY_REPORT)) {
                section = ReportSectionsHelper.getSection(sectionId, predifinedLables);
            } else {
                const complementaryReportIndex = ReportSectionsHelper.extractNumber(sectionId);
                const complementaryReportTitle = annualReport.complementaryReports[complementaryReportIndex]?.title;

                section = ReportSectionsHelper.getComplementaryReportSection(
                    complementaryReportTitle,
                    complementaryReportIndex,
                );
            }
            reportSections.push(section);
        });

        return reportSections;
    }

    private static getSection(sectionId: string, predifinedLabels: Map<string, string>): IReportSection {
        const reportSection = SECTION_ID_TO_SECTION_MAP.get(sectionId);

        const section = cloneDeep<IReportSection>(reportSection);

        if (section == null) {
            throw new Error('This section id does not exist:' + reportSection);
        }
        const localizedSectionTitle = predifinedLabels.get(section.title) as string;

        if (localizedSectionTitle == null) {
            throw new Error('Section Title Localization Key does not exist in localizedTexts Map' + section.title);
        }
        section.title = localizedSectionTitle;
        return section;
    }

    private static getComplementaryReportSection(
        complementaryReportTitle: string,
        complementaryReportIndex: number,
    ): IReportSection {
        return {
            markerClassName: MarkerClasses.COMPLEMENTARY_REPORT + complementaryReportIndex,
            title: complementaryReportTitle,
            id: SectionIds.COMPLEMENTARY_REPORT + complementaryReportIndex,
        };
    }

    public static getOrderedSectionIds(
        printOptions: IDisplayOptions,
        activeArNavigations: AnnualReportStep[],
        complementaryReportsTitles: string[],
        annualReport: IAnnualReport,
        isFixedAssetsScheduleDataValid: boolean,
        complementaryReportsSelection: IDisplayOptions['complementaryReportsSelection'] =
            ReportSectionsHelper.getComplementaryReportsSelection(printOptions),
    ): SectionIds[] {
        const orderOfSectionsIdsAccordingToArNavigation = this.getOrderOfSectionsIdsAccordingToArNavigation(
            printOptions,
            activeArNavigations,
            complementaryReportsTitles,
            annualReport,
            isFixedAssetsScheduleDataValid,
            complementaryReportsSelection,
        );

        return this.handleComplementaryReportsPositions(
            orderOfSectionsIdsAccordingToArNavigation,
            annualReport,
            printOptions,
            complementaryReportsSelection,
        );
    }

    private static getOrderOfSectionsIdsAccordingToArNavigation(
        printOptions: IDisplayOptions,
        activeArNavigations: AnnualReportStep[],
        complementaryReportsTitles: string[],
        annualReport: IAnnualReport,
        isFixedAssetsScheduleDataValid: boolean,
        complementaryReportsSelection?: IDisplayOptions['complementaryReportsSelection'],
    ): SectionIds[] {
        const currentReportSections: SectionIds[] = [];

        activeArNavigations.forEach((navigation) => {
            if (navigation === AnnualReportStep.IMPORT_BALANCE_INCOME) {
                currentReportSections.push(SectionIds.BALANCE);
                currentReportSections.push(SectionIds.INCOME);
            }
            if (navigation === AnnualReportStep.BALANCE_ACCOUNT_PROOF && printOptions.showAccountProofForBalance) {
                currentReportSections.push(SectionIds.APBS);
            }
            if (navigation === AnnualReportStep.INCOME_ACCOUNT_PROOF && printOptions.showAccountProofForIncome) {
                currentReportSections.push(SectionIds.APIS);
            }
            if (navigation === AnnualReportStep.CFS && printOptions.showCfs && annualReport.cashFlowStatement != null) {
                currentReportSections.push(SectionIds.CASHFLOW);
            }
            if (
                navigation === AnnualReportStep.NOTES &&
                printOptions.showNotes &&
                annualReport.notes != null &&
                annualReport.notes.accordions.length !== EMPTY_ARRAY_LENGTH
            ) {
                currentReportSections.push(SectionIds.NOTES);
            }
            if (
                navigation === AnnualReportStep.AOP &&
                printOptions.showAop &&
                annualReport.appropriationOfProfit != null
            ) {
                currentReportSections.push(SectionIds.AOP);
            }
            if (navigation === AnnualReportStep.FINANCIAL_CLOSING_PROCESS && printOptions.showFcp) {
                currentReportSections.push(SectionIds.FINANCIAL_CLOSING_PROCESS);
            }
            if (
                navigation === AnnualReportStep.EMS &&
                printOptions.showEms &&
                annualReport.equityMovementSchedule != null
            ) {
                currentReportSections.push(SectionIds.EMS);
            }
            if (
                navigation === AnnualReportStep.FAS &&
                printOptions.showFas &&
                annualReport.fixedAssetsSchedule != null &&
                isFixedAssetsScheduleDataValid
            ) {
                currentReportSections.push(SectionIds.FAS);
            }
            if (
                navigation === AnnualReportStep.CR &&
                printOptions.showComplementaryReports &&
                annualReport.complementaryReports != null &&
                annualReport.complementaryReports.length > 0
            ) {
                for (let index = 0; index < complementaryReportsTitles.length; index++) {
                    if (
                        !ReportSectionsHelper.isComplementaryReportSelected(
                            complementaryReportsSelection,
                            annualReport.complementaryReports[index],
                            index,
                        )
                    ) {
                        continue;
                    }
                    const complementaryReportId = SectionIds.COMPLEMENTARY_REPORT + index;
                    currentReportSections.push(complementaryReportId as SectionIds);
                }
            }
        });

        return currentReportSections;
    }

    private static handleComplementaryReportsPositions(
        orderOfSectionsIdsAccordingToArNavigation: SectionIds[],
        annualReport: IAnnualReport,
        printOptions: IDisplayOptions,
        complementaryReportsSelection?: IDisplayOptions['complementaryReportsSelection'],
    ): SectionIds[] {
        if (annualReport.complementaryReports == null || !printOptions.showComplementaryReports) {
            return orderOfSectionsIdsAccordingToArNavigation;
        }
        const complementaryReportsIdLocationSortOrder: IComplementaryReportIdLocationSortOrder[] = [];
        annualReport.complementaryReports?.forEach((report, index) => {
            if (!this.isComplementaryReportSelected(complementaryReportsSelection, report, index)) {
                return;
            }
            complementaryReportsIdLocationSortOrder.push({
                id: (SectionIds.COMPLEMENTARY_REPORT + index) as SectionIds,
                sortOrderId: report.sortOrderId,
                printLocation: report.printLocation,
            });
        });
        const complementaryReportsAtTheStart = complementaryReportsIdLocationSortOrder
            .filter((report) => report.printLocation === PrintLocation.START)
            .sort((a, b) => a.sortOrderId - b.sortOrderId);
        const complementaryReportsAtTheEnd = complementaryReportsIdLocationSortOrder
            .filter((report) => report.printLocation === PrintLocation.END)
            .sort((a, b) => a.sortOrderId - b.sortOrderId);
        const complementaryReportsAsAppendix = complementaryReportsIdLocationSortOrder
            .filter((report) => report.printLocation === PrintLocation.APPENDIX)
            .sort((a, b) => a.sortOrderId - b.sortOrderId);

        if (
            complementaryReportsAtTheStart.length === EMPTY_ARRAY_LENGTH &&
            complementaryReportsAtTheEnd.length === EMPTY_ARRAY_LENGTH &&
            complementaryReportsAsAppendix.length === EMPTY_ARRAY_LENGTH
        ) {
            // if complementary reports have no location or there is no complementary reports
            // then we keep the normal order
            return orderOfSectionsIdsAccordingToArNavigation;
        }

        const orderedSections: SectionIds[] = [];

        complementaryReportsAtTheStart.forEach((report) => {
            orderedSections.push(report.id);
        });

        orderOfSectionsIdsAccordingToArNavigation.forEach((sectionId) => {
            // add here any section that is not a complementary report
            if (!sectionId.includes(SectionIds.COMPLEMENTARY_REPORT)) {
                orderedSections.push(sectionId);
            }
        });

        complementaryReportsAtTheEnd.forEach((report) => {
            orderedSections.push(report.id);
        });

        if (complementaryReportsAsAppendix.length > EMPTY_ARRAY_LENGTH) {
            // if there is at least on complementary report in the appendix then we add
            // appendix section
            orderedSections.push(SectionIds.APPENDIX);
        }

        complementaryReportsAsAppendix.forEach((report) => {
            orderedSections.push(report.id);
        });

        return orderedSections;
    }

    private static getOrderOfSectionsIdsAccordingToSavedOrder(
        printOptions: IDisplayOptions,
        activeArNavigations: AnnualReportStep[],
        complementaryReportsTitles: string[],
        annualReport: IAnnualReport,
        complementaryReportsSelection?: IDisplayOptions['complementaryReportsSelection'],
    ): string[] {
        const currentReportSections: string[] = [];
        annualReport.printOptions.reportOrder.forEach((section) => {
            if (section === SectionIds.BALANCE) {
                currentReportSections.push(SectionIds.BALANCE);
            }
            if (section === SectionIds.INCOME) {
                currentReportSections.push(SectionIds.INCOME);
            }
            if (section === SectionIds.APBS && printOptions.showAccountProofForBalance) {
                currentReportSections.push(SectionIds.APBS);
            }
            if (section === SectionIds.APIS && printOptions.showAccountProofForIncome) {
                currentReportSections.push(SectionIds.APIS);
            }
            if (section === SectionIds.CASHFLOW && printOptions.showCfs) {
                currentReportSections.push(SectionIds.CASHFLOW);
            }
            if (section === SectionIds.NOTES && printOptions.showNotes) {
                currentReportSections.push(SectionIds.NOTES);
            }
            if (section === SectionIds.AOP && printOptions.showAop) {
                currentReportSections.push(SectionIds.AOP);
            }
            if (section === SectionIds.FINANCIAL_CLOSING_PROCESS && printOptions.showFcp) {
                currentReportSections.push(SectionIds.FINANCIAL_CLOSING_PROCESS);
            }
            if (section === SectionIds.EMS && printOptions.showEms) {
                currentReportSections.push(SectionIds.EMS);
            }
            if (section === SectionIds.FAS && annualReport.fixedAssetsSchedule != null) {
                currentReportSections.push(SectionIds.FAS);
            }

            if (section.includes(SectionIds.COMPLEMENTARY_REPORT) && printOptions.showComplementaryReports) {
                for (let index = 0; index < complementaryReportsTitles.length; index++) {
                    if (
                        !ReportSectionsHelper.isComplementaryReportSelected(
                            complementaryReportsSelection,
                            annualReport.complementaryReports[index],
                            index,
                        )
                    ) {
                        continue;
                    }
                    currentReportSections.push(SectionIds.COMPLEMENTARY_REPORT + index);
                }
            }
        });

        return currentReportSections;
    }

    static mapSectionIdsToPdfSectionIds(sectionId: SectionIds): PDFSectionIds {
        switch (sectionId) {
            case SectionIds.BALANCE:
                return PDFSectionIds.BALANCE;
            case SectionIds.INCOME:
                return PDFSectionIds.INCOME;
            case SectionIds.CASHFLOW:
                return PDFSectionIds.CASHFLOW;
            case SectionIds.NOTES:
                return PDFSectionIds.NOTES;
            case SectionIds.AOP:
                return PDFSectionIds.AOP;
            case SectionIds.EMS:
                return PDFSectionIds.EMS;
            case SectionIds.FAS:
                return PDFSectionIds.FAS;
            case SectionIds.APBS:
                return PDFSectionIds.APBS;
            case SectionIds.APIS:
                return PDFSectionIds.APIS;
            case SectionIds.FINANCIAL_CLOSING_PROCESS:
                return PDFSectionIds.FCP;
            case SectionIds.APPENDIX:
                return PDFSectionIds.APPENDIX;
            default: {
                if ((sectionId as string).includes(SectionIds.COMPLEMENTARY_REPORT)) {
                    const complementaryReportIndex = ReportSectionsHelper.extractNumber(sectionId);
                    return (PDFSectionIds.COMPLEMENTARY_REPORT + complementaryReportIndex) as PDFSectionIds;
                }

                throw new Error(`Unknown section ID: ${sectionId}`);
            }
        }
    }

    public static extractNumber(id: string): number | null {
        const match = id.match(/\d+$/);
        return match ? parseInt(match[0], 10) : null;
    }

    public static isComplementaryReportSelected(
        selections: IDisplayOptions['complementaryReportsSelection'] | undefined,
        report: IComplementaryReport | undefined,
        index: number,
    ): boolean {
        if (selections == null || selections.length === EMPTY_ARRAY_LENGTH) {
            return true;
        }

        if (report?.fileId) {
            const byFileId = selections.find((selection) => selection.fileId === report.fileId);
            if (byFileId != null) {
                return byFileId.includeInReport;
            }
        }

        const byIndex = selections.find((selection) => selection.index === index);
        return byIndex?.includeInReport ?? true;
    }
}
